export type TranslationSegment =
  | string
  | {
      contexts: Array<[boolean, string]> // [when, context]
      default: string
    }

interface TranslationKeyConfig {
  namespace?: string
  segments: TranslationSegment[]
}

/**
 * Builds a translation key based on multiple conditions and segments
 *
 * @param config Configuration object for building the translation key
 * @returns The fully constructed translation key
 *
 * @description
 * This function constructs translation keys dynamically based on conditional logic.
 *
 * Key behaviors:
 * - Segments can be either fixed strings or conditional objects
 * - For conditional segments, the first matching context (where condition is true) is used
 * - If no conditions match, the default value is used
 * - Namespace is prefixed with ':' separator, all other segments use '.' separator
 *
 * @example
 * // Basic usage with fixed segments
 * buildTranslationKey({
 *   namespace: 'summary',
 *   segments: ['autopay', 'modal', 'title']
 * })
 * // Returns: "summary:autopay.modal.title"
 *
 * @example
 * // Conditional segments with first-match priority
 * buildTranslationKey({
 *   namespace: 'summary',
 *   segments: [
 *     'autopay',
 *     {
 *       contexts: [
 *         [isAFF, 'modal_aff'],              // Checked first
 *         [isPaymentMonthly, 'modal_paymentMonthly'] // Checked second
 *       ],
 *       default: 'modal'                     // Used if no conditions match
 *     }
 *   ]
 * })
 * // When isAFF is true: "summary:autopay.modal_aff"
 * // When isAFF is false and isPaymentMonthly is true: "summary:autopay.modal_paymentMonthly"
 * // When both are false: "summary:autopay.modal"
 *
 * @example
 * // Multiple conditional segments
 * buildTranslationKey({
 *   namespace: 'summary',
 *   segments: [
 *     'autopay',
 *     {
 *       contexts: [
 *         [isAFF, 'modal_aff'],
 *         [isPaymentMonthly, 'modal_paymentMonthly']
 *       ],
 *       default: 'modal'
 *     },
 *     {
 *       contexts: [
 *         [isPaymentMonthly, `cta_${autopayToggleExperimentValue}`]
 *       ],
 *       default: 'cta'
 *     }
 *   ]
 * })
 * // When isAFF is true and isPaymentMonthly is false:
 * //   "summary:autopay.modal_aff.cta"
 * // When isPaymentMonthly is true and autopayToggleExperimentValue is 'A':
 * //   "summary:autopay.modal_paymentMonthly.cta_A"
 */
export function buildTranslationKey(config: TranslationKeyConfig): string {
  const { namespace, segments } = config

  const keyParts: string[] = []

  if (namespace) {
    keyParts.push(namespace)
  }

  for (const segment of segments) {
    if (typeof segment === 'string') {
      keyParts.push(segment)
    } else {
      const matchedContext = segment.contexts.find(([when]) => when)
      const segmentValue = matchedContext != null ? matchedContext[1] : segment.default
      keyParts.push(segmentValue)
    }
  }

  if (keyParts.length === 0) {
    return ''
  }

  if (namespace && keyParts.length > 1) {
    return `${keyParts[0]}:${keyParts.slice(1).join('.')}`
  }

  return keyParts.join('.')
}
